import {
    buildFieldSortCriterion,
    buildInteractiveResult,
    buildQuerySummary,
    buildResultList,
    buildResultsPerPage,
    buildSort,
    loadAdvancedSearchQueryActions,
    loadQueryActions,
    loadSearchActions,
    loadSearchAnalyticsActions,
    SortOrder,
} from '@coveo/headless';
import { getHeadlessEngine } from 'libs/coveo/getHeadlessEngine';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

const defaultValue = {
    coveo: {
        engine: null,
        controller: {
            searchBox: null,
            querySummary: null,
            resultList: null,
        },
        action: {
            updateQuery: null,
            executeSearch: null,
        },
    },
    results: [],
    resultListState: {},
    noResultsComponent: null,
    errorComponent: null,
};

const DirectoryContext = createContext(defaultValue);

export const DirectoryProvider = (props) => {
    const { children, coveo, locationId, noResultsComponent, errorComponent } = props;
    const history = useHistory();
    const formatResults = (list) =>
        list.map((s) => {
            const interactiveResultController = engine
                ? buildInteractiveResult(engine, {
                      options: { result: s },
                  })
                : null;
            return {
                id: s.uniqueId,
                href: s.uri,
                ariaLabel: s?.raw?.locname || s.title,
                label: s?.raw?.locname || s.title,
                target: null,
                itemDescription: '',
                csr: false,
                coveoAnalyticsCallbacks: interactiveResultController
                    ? {
                          cancelPendingSelect: () =>
                              interactiveResultController.cancelPendingSelect(),
                          onClick: () => interactiveResultController.select(),
                          onContextMenu: () => interactiveResultController.select(),
                          onMouseDown: () => interactiveResultController.select(),
                          onMouseUp: () => interactiveResultController.select(),
                          onTouchStart: () => interactiveResultController.beginDelayedSelect(),
                          onTouchEnd: () => interactiveResultController.cancelPendingSelect(),
                      }
                    : undefined,
            };
        });

    const [
        engine,
        QueryActionCreators,
        SearchAnalyticsActionCreators,
        SearchActionCreators,
        AdvancedSearchActionCreators,
        controllerQuerySummary,
        controllerResultList,
    ] = useMemo(() => {
        const engine = getHeadlessEngine(`directory-${locationId}}`, {
            ...coveo,
            analytics: { analyticsMode: 'legacy', trackingId: 'featured-locations' },
        });
        const QueryActionCreators = loadQueryActions(engine);
        const SearchAnalyticsActionCreators = loadSearchAnalyticsActions(engine);
        const SearchActionCreators = loadSearchActions(engine);
        const AdvancedSearchActionCreators = loadAdvancedSearchQueryActions(engine);
        const controllerQuerySummary = buildQuerySummary(engine);
        const controllerResultList = buildResultList(engine, {
            options: {
                fieldsToInclude: ['filetype', 'locname', 'locparentid', 'locrootid'],
            },
        });
        buildResultsPerPage(engine, {
            initialState: { numberOfResults: 9999 },
        });
        buildSort(engine, {
            initialState: { criterion: buildFieldSortCriterion('title', SortOrder.Ascending) },
        });
        return [
            engine,
            QueryActionCreators,
            SearchAnalyticsActionCreators,
            SearchActionCreators,
            AdvancedSearchActionCreators,
            controllerQuerySummary,
            controllerResultList,
        ];
    }, [coveo, locationId]);

    const [resultListState, setResultListState] = useState(controllerResultList.state);
    const [results, setResults] = useState(formatResults(controllerResultList.state.results));

    const executeSearch = useCallback(() => {
        return SearchActionCreators.executeSearch(
            SearchAnalyticsActionCreators.logSearchboxSubmit(),
        );
    }, [SearchActionCreators, SearchAnalyticsActionCreators]);

    const triggerExecuteSearch = useCallback(
        (searchValue) => {
            engine.dispatch(
                QueryActionCreators.updateQuery({
                    q: searchValue || '',
                }),
            );
            engine.dispatch(
                AdvancedSearchActionCreators.registerAdvancedSearchQueries({
                    aq: `@locparentid==${locationId} OR @locrootid==${locationId}`,
                    cq: '@filetype==Location AND NOT @source=="AHS Locations" AND NOT @source=="AMG Find-A-Doc YouTube Playlist" AND NOT @source=="Kyruus AMG Providers"',
                }),
            );

            engine.dispatch(executeSearch());
        },
        [QueryActionCreators, AdvancedSearchActionCreators, engine, executeSearch, locationId],
    );

    const handleSubmit = useCallback(
        (searchValue) => {
            const newSearchParams = new URLSearchParams(history.location.search);

            if (searchValue) {
                newSearchParams.set('term', searchValue);
            } else {
                newSearchParams.delete('term');
            }

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;
            history.push(newUrl);
            triggerExecuteSearch(searchValue);
        },
        [history, triggerExecuteSearch],
    );

    useEffect(() => {
        const unsubscribe = controllerResultList.subscribe(() => {
            setResultListState(controllerResultList.state);

            const newResults = formatResults(controllerResultList.state.results);

            setResults(newResults);
        });
        return () => {
            // Unsubscribe from the results list
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controllerResultList]);

    useEffect(() => {
        triggerExecuteSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(() => {
        return {
            coveo: {
                controller: {
                    querySummary: controllerQuerySummary,
                    resultList: controllerResultList,
                },
            },
            handleSubmit,
            triggerExecuteSearch,
            results,
            resultListState,
            noResultsComponent,
            errorComponent,
        };
    }, [
        controllerQuerySummary,
        controllerResultList,
        handleSubmit,
        triggerExecuteSearch,
        results,
        resultListState,
        errorComponent,
        noResultsComponent,
    ]);

    return <DirectoryContext.Provider value={value}>{children}</DirectoryContext.Provider>;
};

export const useDirectoryContext = () => {
    const context = React.useContext(DirectoryContext);

    if (context == null) {
        throw new Error(`'useDirectoryContext' needs to be within 'DirectoryContext'`);
    }

    return context;
};
